import { useState } from 'react';
import { Alert, Button, Skeleton, Stack } from '@mui/material';
import { useNotify } from '@/components/feedback/notificationsContext';
import { useAsync } from '@/hooks/useAsync';
import { cancelOwnPlan, getMyBilling } from './api';
import { BillingPanel, ChangePlanDialog, ReasonDialog } from './BillingPanel';
import { formatDate } from './labels';
import type { InstitutionBillingDetail } from './types';

/**
 * The plans of the institutions the coordinator runs: status, invoices, history, and the
 * buttons to contract, change or cancel. `onlyBlocked`: just those without access now.
 */
export function MyBilling({
  onlyBlocked,
  onChanged,
}: {
  onlyBlocked?: boolean;
  onChanged?: () => void;
}) {
  const notify = useNotify();
  const billing = useAsync(getMyBilling);
  const [changing, setChanging] = useState<InstitutionBillingDetail | null>(null);
  const [canceling, setCanceling] = useState<InstitutionBillingDetail | null>(null);

  const reload = () => {
    billing.reload();
    onChanged?.();
  };

  if (billing.error) return <Alert severity="error">Não foi possível carregar o plano.</Alert>;
  if (billing.loading && !billing.data) return <Skeleton variant="rounded" height={280} />;
  const list = (billing.data ?? []).filter((b) => !onlyBlocked || !b.hasAccess);
  if (list.length === 0) {
    return <Alert severity="info">Nenhuma instituição para mostrar.</Alert>;
  }

  return (
    <Stack spacing={4}>
      {list.map((b) => {
        const sub = b.subscription;
        const openCourtesy = b.status === 'courtesy' && !sub?.endsAt;
        const paid = sub?.kind === 'paid' && !sub.canceledAt;
        return (
          <BillingPanel
            key={b.id}
            billing={b}
            actions={
              <>
                {!openCourtesy && !b.blocked && (
                  <Button
                    variant={b.hasAccess ? 'outlined' : 'contained'}
                    onClick={() => setChanging(b)}
                  >
                    {paid ? 'Mudar plano ou nº de estudantes' : 'Contratar plano'}
                  </Button>
                )}
                {paid && (
                  <Button color="error" onClick={() => setCanceling(b)}>
                    Cancelar plano
                  </Button>
                )}
              </>
            }
          />
        );
      })}
      {changing && (
        <ChangePlanDialog
          open
          billing={changing}
          onClose={() => setChanging(null)}
          onDone={() => {
            setChanging(null);
            reload();
          }}
        />
      )}
      <ReasonDialog
        open={Boolean(canceling)}
        title={`Cancelar o plano de ${canceling?.name ?? ''}?`}
        description={
          <>
            As cobranças param. O acesso continua até{' '}
            <b>
              {formatDate(
                canceling?.subscription?.paidUntil ??
                  canceling?.subscription?.trialEndsAt ??
                  canceling?.accessUntil,
              )}
            </b>{' '}
            e depois estudantes e professores deixam de entrar. Nada é apagado: ao contratar de
            novo, tudo volta como estava.
          </>
        }
        confirmLabel="Cancelar plano"
        danger
        onClose={() => setCanceling(null)}
        onConfirm={async (reason) => {
          await cancelOwnPlan(canceling!.id, reason);
          notify('Plano cancelado.', 'success');
          setCanceling(null);
          reload();
        }}
      />
    </Stack>
  );
}
